import { createContext, useContext, useEffect, useLayoutEffect, useRef, } from 'react'
import useUnreadMessagesContext from './unreadMessagesContext'
import useInfiniteLoading from '@hooks/useInfiniteLoading'
import { MapView } from '@contexts/mapDataContext'
import { useAuthContext } from './authContext'
import { useNavigate } from 'react-router-dom'
import useCableContext from './cableContext'
import axios from '@utils/webRequests'

const sortByUpdatedAt = (conversations) => conversations.sort((a,b) => (
  new Date(b.updatedAt) - new Date(a.updatedAt)
))

const ConversationsContext = createContext()
const ConversationsProvider = ({ children }) => {
  const { CableEmitter, conversationsRefreshRequestRef } = useCableContext()
  const { state: authState, authenticated } = useAuthContext()
  const allDataRequest = ({ offset }) => axios.get('/api/my/conversations', { params: { offset: offset || 0 } })
  const favoriteDataRequest = ({ offset }) => axios.get('/api/my/conversations', { params: { offset: offset || 0, favorite: true, include_new: true } })
  const { userReceivedUnreadMessages, addSeenMessage, isMessageSeen, } = useUnreadMessagesContext()
  const allConversationData = useInfiniteLoading({ itemName: 'all', dataRequest: allDataRequest, createSet: true, afterGetInitialItems })
  const favoriteConversationData = useInfiniteLoading({ itemName: 'favorites', dataRequest: favoriteDataRequest, createSet: true })
  const pendingCovnersationRequests = useRef(new Set())
  const didGetInitialConversations = useRef(false)
  const navigate = useNavigate()

  // Handles websocket information, including websocket events for new conversations
  // AND new messages (with and without an existing conversation)
  useEffect(() => {
    console.log('INITIALIZING CONVERSATION DATA LISTNERS')
    CableEmitter.addEventListener('UserMessage', (data) => {
      const currentConversationId = new URLSearchParams(window.location.search).get('conversation_id')
      const isViewingConversation = !!(currentConversationId && data?.message?.conversationUuid && (currentConversationId === data?.message?.conversationUuid))
      const conversationExists = allConversationData?.itemsSet?.current?.has?.(data?.message?.conversationUuid)
      if (data?.action === 'create:conversation') {
        data?.conversation && addConversationToAll(data?.conversation)
        handleUnreadMessagesForData(data, isViewingConversation)
      } else if (data?.action === 'create' && data?.message?.conversationUuid) {
        handleUnreadMessagesForData(data, isViewingConversation)
        if (conversationExists) {
          handleNewMessageEventWhenConversationExists(data.message)
          // If not currently viewing the conversation we got a new message for, attempt to update the localstorage state with that message
          if (!currentConversationId && data.message.conversationUuid) {
            try {
              const accessor = `messages-${data.message.conversationUuid}`
              let messages = JSON.parse(localStorage.getItem(accessor) || null)
              if (Array.isArray(messages)) {
                localStorage.setItem(accessor, addMetaStateVar([data.message]).concat(closeAllMetaData(messages)))
              }
            } catch {}
          }
        } else {
          getConversationFromID(data.message.conversationUuid)
        }
      } else if (data?.action === 'destroy:conversation' && data?.conversation_uuid) {
        removeConversationFromAll(data.conversation_uuid)
        // Below we're navigating the user to the conversations screen if they are viewing the current conversation being deleted
        // WARNING|NOTE: The reason we aren't using the searchParams from react router here is mostly related to stale state
        //               You tried multiple ways of accessing the state, including updater functions, and they all have issues
        if (isViewingConversation) {
          navigate('/conversations', { replace: true })
        }
      }
    })
  }, [])

  // Gets initial conversations on load once user is authneticated
  useLayoutEffect(() => {
    if (!didGetInitialConversations.current && authenticated) {
      allConversationData.getInitialItems()
      favoriteConversationData.getInitialItems()
      didGetInitialConversations.current = true
    }
  }, [authenticated])

  // sets method to be called for refreshing conversations when connection drops and is re-established
  useEffect(() => {
    // We need the user to be present and accurate to not have state user data during the unseen message check
    if (authState?.user) {
      conversationsRefreshRequestRef.current = () => {
        allConversationData.getInitialItems()
        favoriteConversationData.getInitialItems()
      }
    }
  }, [authState?.user])

  function afterGetInitialItems(conversations) {
    const maxLength = conversations.length < 3 ? conversations.length : 3
    let conversationId
    let recentMessage
    let recentMessageId
    let recentMessageUserId
    for (let i = 0; i < maxLength; i++) {
      conversationId = conversations[i]?.uuid
      recentMessage = conversations[i]?.mostRecentMessage
      recentMessageId = recentMessage?.uuid
      recentMessageUserId = recentMessage?.userUuid
      if (isMessageSeen(conversationId, recentMessage)) continue;
      else if (recentMessageUserId && recentMessageUserId !== authState?.user?.nearbyUuid) {
        userReceivedUnreadMessages(conversationId, recentMessageId)
        break
      }
    }
  }

  const handleUnreadMessagesForData = (data, isViewingConversation) => {
    const conversation = data.conversation
    const message = data.message
    const user = message?.user
    const otherUserId = user?.nearbyUuid
    if (!conversation || !message || !user) return;
    if (isViewingConversation) {
      // If the user is viewing the curernt conversation
      // New SEEN reply on map screen
      MapView.setUserHasNewSeenReply(otherUserId)
      // We add a seen message to the unreadMessages context
      addSeenMessage(conversation.uuid, message?.uuid)
      // If the user is viewing the current conversation when we get the new message, update the seen_at status immediately and send ajax request to update data on backend
      // TODO: We need to throttle this as well so we aren't sending it for every single message. It can be once every second (or couple seconds) The throttling also needs to be specific to every conversation
      message.seenAt = new Date().toISOString()
      axios.put(`api/my/conversations/${data.conversation.uuid}/unread_messages`).catch(console.log)
    } else {
      // If the user is not viewing the conversation
      // The user gets a new unseen reply on the map
      MapView.setUserHasNewReply(otherUserId)
      // We set the unread Messages notification on with new unread message
      userReceivedUnreadMessages(conversation.uuid, message?.uuid)
    }
  }

  const closeAllMetaData = (messages) => {
    return messages.map((message) => {
      message.isMetaDataOpen = false
      return message
    })
  }

  const addMetaStateVar = (messages) => messages.map((message, index) => {
    if(!message?.hasOwnProperty('isMetaDataOpen')) {
      if(index === 0) {
        message.isMetaDataOpen = true
      } else {
        message.isMetaDataOpen = false
      }
    } 
    return message
  })

  const handleNewMessageEventWhenConversationExists = (message) => {
    const { updatedConversation, newConversations } = updateConversationWithNewMessageEvent(allConversationData, message)
    if (updatedConversation) {
      allConversationData.updateItem(() => [updatedConversation, ...newConversations])
      if (updatedConversation?.isFavorite) {
        // If the conversation is favorited, do the same thing for the favorite conversations
        const {
          updatedConversation: updatedFavoriteConversation,
          newConversations: newFavoriteConversations,
        } = updateConversationWithNewMessageEvent(favoriteConversationData, message)
        // Update Favorites with new information
        if (updatedFavoriteConversation) {
          favoriteConversationData.updateItem(() => (
            [updatedFavoriteConversation, ...newFavoriteConversations]
          ))
        }
      }
    } else {
      console.warn('conversationsContext: conversation set said the conversation existed in memory, but the conversation was not found')
      getConversationFromID(message.conversationUuid)
    }
  }

  const updateConversationWithNewMessageEvent = (data, message) => {
    let updatedConversation
    const newConversations = data.itemsRef?.current?.filter((conversation) => {
      if (conversation.uuid === message.conversationUuid) {
        conversation.mostRecentMessage = message
        // The new conversation updatedAt will be approximately the same value..
        conversation.updatedAt = message.createdAt
        updatedConversation = conversation
        return false
      } else return true
    })
    return { updatedConversation, newConversations }
  }

  const getConversationFromID = (conversationUuid) => {
    if (pendingCovnersationRequests.current.has(conversationUuid)) return;
    pendingCovnersationRequests.current.add(conversationUuid)
    axios.get(`/api/my/conversations/${conversationUuid}`)
    .then((res) => {
        if (res?.data && res.data.isFavorite) {
          allConversationData.prependItem(res.data)
          favoriteConversationData.prependItem(res.data)
        } else if (res.data) {
          allConversationData.prependItem(res.data)
        }
        pendingCovnersationRequests.current.delete(conversationUuid)
      })
      .catch(console.log)
  }
  
  const removeConversation = (data, conversationUuid) => {
    // Remove conversation from favorites data
    data.removeItem((conversations) => {
      // removeItem takes a callback that expects the
      // new state with the removed item to be returned
      return conversations.filter((oldConversation) => (
        oldConversation?.uuid !== conversationUuid
      ))
    })
  }

  const removeConversationFromFavorites = (conversationUuid) => {
    removeConversation(favoriteConversationData, conversationUuid)
  }

  const removeConversationFromAll = (conversationUuid) => {
    removeConversation(allConversationData, conversationUuid)
    if (favoriteConversationData.itemsSet.current.has(conversationUuid)) {
      removeConversationFromFavorites(conversationUuid)
    }
  }

  const removeFromFavoritesAndUpdateInAll = (conversationUuid) => {
    // Remove conversation from favorites data
    removeConversationFromFavorites(conversationUuid)
    // Update isFavorite status in all conversations
    allConversationData.updateItem((conversations) => {
      return conversations.map((oldConversation) => {
        if (oldConversation.uuid === conversationUuid) {
          oldConversation.isFavorite = false
        }
        return oldConversation
      })
    })
  }

  const addConversationToFavorites = (conversation) => {
    // Add conversation to favorites
    // we actually need to sort these..
    favoriteConversationData.updateItem((conversations) => {
      return sortByUpdatedAt(conversations.concat(conversation))
    })
    // Update favorite status in all conversations
    allConversationData.updateItem((conversations) => {
      return conversations.map((oldConversation) => {
        if (oldConversation.uuid === conversation.uuid) {
          return conversation
        } else  {
          return oldConversation
        }
      })
    })
  }

  // // NOTE: This action is basically the "delete" functionality
  // const removeConversationFromAll = (conversationUuid) => {
  //   allConversationData.removeItem((conversations) => {
  //     // removeItem takes a callback that expects the
  //     // new state with the removed item to be returned
  //     return conversations.filter((oldConversation) => (
  //       oldConversation?.uuid !== conversationUuid
  //     ))
  //   })
  //   removeConversationFromFavorites(conversationUuid)
  // }

  const addConversationToAll = (conversation) => {
    allConversationData?.prependItem(conversation)
  }

  const _updateMessageRead = (data, conversation, isRead=true) => {
    data.updateItem((conversations) => {
      const newConversations = conversations.map((oldConversation) => {
        if (oldConversation.uuid === conversation.uuid) {
          // converting to ISO string to match rails format
          if (oldConversation.mostRecentMessage) {
            oldConversation.mostRecentMessage.seenAt = isRead ? new Date().toISOString() : null
          }
        }
        return oldConversation
      })
      return newConversations
    })
  }

  const updateMessageReadStatus = (conversation, otherUserUuid, isRead=true) => {
    if (isRead && otherUserUuid) {
      // Updates profile on map and grid so we see the reply was read
      MapView.setUserHasReadReply(otherUserUuid)
    }
    // Update read status (seen_at) for all conversations
    _updateMessageRead(allConversationData, conversation, isRead)

    // If it's a favorite, update seen_at status there as well
    if (conversation.isFavorite) {
      _updateMessageRead(favoriteConversationData, conversation, isRead)
    }
  }

  const _sendToTop = (data, conversation, replaceTime=false) => {
    data.updateItem((conversations) => {
      const newConversations = conversations.map((oldConversation) => {
        if (oldConversation.uuid === conversation.uuid) {
          // converting to ISO string to match rails format
          if (replaceTime) conversation.updatedAt = new Date().toISOString()
          return conversation
        } else  {
          return oldConversation
        }
      })
      return sortByUpdatedAt(newConversations)
    })
  }

  const sendConversationToTop = (conversation, replaceTime=false) => {
    // Send to top in all conversations
    if (allConversationData.itemsIndex?.current?.[conversation.uuid] !== 0) {
      _sendToTop(allConversationData, conversation, replaceTime)
    }
    // If it's a favorite, send to top in favorites conversations as well
    if (conversation.isFavorite) {
      _sendToTop(favoriteConversationData, conversation, replaceTime)
    }
  }

  return (
    <ConversationsContext.Provider value={{
      allConversationData,
      favoriteConversationData,
      addConversationToAll,
      addConversationToFavorites,
      removeConversationFromAll,
      removeConversationFromFavorites,
      removeFromFavoritesAndUpdateInAll,
      sendConversationToTop,
      updateMessageReadStatus,
    }}>
      {children}
    </ConversationsContext.Provider>
  )
}

const useConversationsContext = () => useContext(ConversationsContext)

export default useConversationsContext

export { ConversationsContext, ConversationsProvider, useConversationsContext }
